import React, {
    useEffect,
    useRef,
    useCallback,
    useState,
    useLayoutEffect,
    useMemo,
} from 'react'
import { useSelector } from 'react-redux'
import { debounce } from 'lodash'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import 'quill-mention/dist/quill.mention.css'
import { Mention, MentionBlot } from 'quill-mention'
import { message } from 'antd'
import { Icon } from '@iconify/react'
import styles from './DescriptionEditor.module.css'
import QuillInsertDropdown from '../../../../components/MentionEditor/QuillInsertDropdown'
import { buildInsertSections } from '../../../../components/MentionEditor/quillInsertMenuUtils'
import assigneeStyles from '../AssigneeSelector.module.css'
import { loginState } from '../../../login/login.slice'
import { userListForClient } from '../../../users/users.service'
import {
    resolveAppLinkTitle,
    shouldResolveAppLinkPaste,
    formatAppLinkLabel,
    formatAppLinkTooltip,
    toAppLinkFormats,
    parseAppDeepLink,
    getLinkHref,
    linksShareHref,
} from './resolveAppLinkTitle'
import AppLinkPicker from './AppLinkPicker'
import { loadProjectStoryMapLinkCatalog } from './loadProjectStoryMapLinkCatalog'
import {
    looksLikeMarkdown,
    markdownToHtml,
    shouldConvertPastedMarkdown,
} from './markdownToHtml'
import MediaViewer from '../../../taskRefactored/components/mediaViewer'

const findCatalogItemForLink = (items, href, kind) => {
    const list = Array.isArray(items) ? items : []
    const target = String(href || '')
    if (!target || list.length === 0) return null
    const exact = list.find((item) => item?.href === target)
    if (exact) return exact
    return (
        list.find(
            (item) =>
                item?.kind === kind &&
                item?.id &&
                (target.includes(`storyId=${item.id}`) ||
                    target.includes(`stepId=${item.id}`))
        ) || null
    )
}

// ── Register blots once (try/catch guards against HMR double-registration) ────
try {
    const BlockEmbed = Quill.import('blots/block/embed')
    class DividerBlot extends BlockEmbed {}
    DividerBlot.blotName = 'divider'
    DividerBlot.tagName = 'hr'
    Quill.register(DividerBlot)
} catch (_) {}

// Hover tooltip on links via title= DOM attribute (kept as its own Quill format).
try {
    const Parchment = Quill.import('parchment')
    const AppLinkTitle = new Parchment.Attributor.Attribute(
        'appLinkTitle',
        'title',
        { scope: Parchment.Scope.INLINE_ATTRIBUTE }
    )
    Quill.register(AppLinkTitle, true)
} catch (_) {}

try {
    Quill.register('modules/mention', Mention)
    Quill.register('formats/mention', MentionBlot)
} catch (_) {}

// ── Bullet lists as <ul>, ordered/checked as <ol> ────────────────────────────
// Quill 2.x renders every list inside an <ol> (the type lives in `data-list`).
// We split the container by type so bullet lists are semantic <ul> elements,
// which also renders correctly anywhere the description HTML is shown without
// Quill's own marker CSS.
try {
    const ListContainerBase = Quill.import('blots/container')
    const ListItemBase = Quill.import('formats/list') // default export = ListItem

    class OrderedListContainer extends ListContainerBase {}
    OrderedListContainer.blotName = 'ordered-list-container'
    OrderedListContainer.tagName = 'OL'

    class BulletListContainer extends ListContainerBase {}
    BulletListContainer.blotName = 'bullet-list-container'
    BulletListContainer.tagName = 'UL'

    class ListItem extends ListItemBase {
        optimize(context) {
            const isBullet =
                this.domNode.getAttribute('data-list') === 'bullet'
            const RequiredContainer = isBullet
                ? BulletListContainer
                : OrderedListContainer
            // Point the item at the container matching its current type, then
            // rewrap if it's currently sitting in the wrong one.
            ListItem.requiredContainer = RequiredContainer
            if (
                this.parent == null ||
                this.parent.statics.blotName !== RequiredContainer.blotName
            ) {
                this.wrap(RequiredContainer.blotName)
            }
            super.optimize(context)
        }
    }
    ListItem.blotName = 'list'
    ListItem.tagName = 'LI'
    ListItem.requiredContainer = OrderedListContainer
    OrderedListContainer.allowedChildren = [ListItem]
    BulletListContainer.allowedChildren = [ListItem]

    Quill.register(OrderedListContainer, true)
    Quill.register(BulletListContainer, true)
    Quill.register(ListItem, true)
} catch (_) {}

// ── Mention helpers (mirrors MentionEditor.js) ────────────────────────────────
const MENTION_HINT_ID = '__mention_hint__'
const MENTION_LOADING_MORE_ID = '__mention_loading_more__'
const MENTION_PAGE_LIMIT = 10
const MENTION_SKIP_IDS = new Set([MENTION_HINT_ID, MENTION_LOADING_MORE_ID])

/**
 * Description save payload, all from one editor state:
 * `description` plain text (mentions as `@Name`), `descriptionRich` editor
 * HTML, `descriptionMentionId` every mention currently in the text.
 */
export const extractDescriptionMentionState = (quill, html) => {
    const descriptionRich =
        html ?? normalizeHtml(quill?.root?.innerHTML || '')
    const seen = new Set()
    const descriptionMentionId = []
    let plainText = ''

    quill?.getContents?.()?.ops?.forEach((op) => {
        if (!op || op.insert == null) return
        if (typeof op.insert === 'string') {
            plainText += op.insert
            return
        }
        const mention = op.insert.mention
        if (!mention) return
        const name = String(mention.value || '')
            .replace(/^@/, '')
            .trim()
        if (name) plainText += `@${name}`
        const id = mention.id != null ? String(mention.id) : ''
        if (!id || MENTION_SKIP_IDS.has(id) || seen.has(id)) return
        seen.add(id)
        descriptionMentionId.push(id)
    })

    return {
        description: plainText.replace(/\n+$/, '').trim(),
        descriptionRich,
        descriptionMentionId,
    }
}

/** The editor shows `descriptionRich`; tasks saved before it existed fall back to `description`. */
export const pickDescriptionEditorValue = (task) => {
    if (typeof task?.descriptionRich === 'string' && task.descriptionRich)
        return task.descriptionRich
    return typeof task?.description === 'string' ? task.description : ''
}

const getMentionInitials = (name) => {
    const text = name == null ? '' : String(name).trim()
    if (!text) return 'U'
    const parts = text.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
        const a = parts[0][0],
            b = parts[1][0]
        if (a != null && b != null) return `${a}${b}`.toUpperCase()
    }
    return text.slice(0, 2).toUpperCase()
}

const getMentionAvatarColor = (name) => {
    if (!name) return '#e2e8f0'
    const colors = [
        '#85BBF9',
        '#6366f1',
        '#8b5cf6',
        '#ec4899',
        '#f59e0b',
        '#10b981',
        '#06b6d4',
    ]
    return colors[name.charCodeAt(0) % colors.length]
}

// ── Static insert-menu definition ─────────────────────────────────────────────
const INSERT_ITEMS = [
    { key: 'paragraph', label: 'Paragraph', icon: 'ri:paragraph' },
    { key: 'header1', label: 'Heading 1', icon: 'ri:h-1' },
    { key: 'header2', label: 'Heading 2', icon: 'ri:h-2' },
    { type: 'sep' },
    { key: 'bullet', label: 'Bulleted list', icon: 'ri:list-unordered' },
    { key: 'ordered', label: 'Numbered list', icon: 'ri:list-ordered' },
    { type: 'sep' },
    { key: 'quote', label: 'Quote', icon: 'ri:double-quotes-l' },
    { key: 'code', label: 'Code block', icon: 'ri:code-box-line' },
    { key: 'table', label: 'Table', icon: 'ri:table-line' },
    { key: 'sectionBreak', label: 'Section break', icon: 'ri:separator' },
    { type: 'sep' },
    { key: 'emoji', label: 'Emoji', icon: 'ri:emoji-sticker-line' },
    { key: 'image', label: 'Image', icon: 'ri:image-line' },
    { key: 'mention', label: 'Mention', icon: 'ri:at-line' },
    { key: 'appLink', label: 'Story / Step link', icon: 'ri:hashtag' },
    { key: 'link', label: 'Embed link', icon: 'ri:link' },
]

const buildDescriptionInsertSections = ({
    enableMentions = true,
    enableAppLinks = true,
} = {}) =>
    buildInsertSections(
        INSERT_ITEMS.filter((item) => {
            if (item.key === 'mention' && !enableMentions) return false
            if (item.key === 'appLink' && !enableAppLinks) return false
            return true
        })
    )

const EMPTY_HTML = '<p><br></p>'
export const normalizeHtml = (html) => {
    // Meta flags (true) / non-strings are not HTML content
    if (typeof html !== 'string') return ''
    if (!html || html === EMPTY_HTML) return ''
    return html
}

const stripDescriptionText = (html = '') =>
    String(html ?? '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

/** Media src list so image-only descriptions are not treated as empty. */
const extractMediaSignatures = (html = '') => {
    const sigs = []
    const re = /<(?:img|video)[^>]*\ssrc=["']([^"']+)["'][^>]*>/gi
    let match
    while ((match = re.exec(String(html)))) {
        sigs.push(match[1])
    }
    return sigs.join('\n')
}

/** True when two description values are the same for display/storage purposes. */
export const descriptionsAreEqual = (a, b) => {
    const normA = normalizeHtml(a)
    const normB = normalizeHtml(b)
    if (normA === normB) return true
    const textA = stripDescriptionText(normA || a)
    const textB = stripDescriptionText(normB || b)
    if (textA !== textB) return false
    return (
        extractMediaSignatures(normA || a) ===
        extractMediaSignatures(normB || b)
    )
}

export const toQuillHtml = (value = '') => {
    const text = String(value).trim()
    if (!text) return ''
    if (/<[a-z][\s\S]*>/i.test(text)) return text
    if (looksLikeMarkdown(text)) return markdownToHtml(text)
    return `<p>${text}</p>`
}

const ICON_SIZE = 16

const DescriptionEditor = ({
    value = '',
    onSave,
    placeholder = 'Add a description...',
    projectId,
    readOnly = false,
    linkCatalog = null,
    enableMentions = true,
    enableAppLinks = true,
    mentionList = 'support',
    isActive = true,
}) => {
    const { userProfile } = useSelector(loginState)
    const closeMentionListRef = useRef(() => {})

    const wrapperRef = useRef(null)
    const mountRef = useRef(null)
    const quillRef = useRef(null)
    const onSaveRef = useRef(onSave)
    const readOnlyRef = useRef(readOnly)
    const valueRef = useRef(value)
    const savedRef = useRef('')
    const hasUserEditedRef = useRef(false)
    const isUnmountingRef = useRef(false)
    const isFocusedRef = useRef(false)
    const linkCatalogRef = useRef(linkCatalog)
    const appLinkItemsRef = useRef(
        Array.isArray(linkCatalog) ? linkCatalog : []
    )
    const enableMentionsRef = useRef(enableMentions)
    const enableAppLinksRef = useRef(enableAppLinks)
    const mentionListRef = useRef(
        mentionList === 'tasks' || mentionList === 'main'
            ? mentionList
            : 'support'
    )
    const fetchMentionUsersRef = useRef(async () => [])
    const getMentionListForRenderRef = useRef(() => [])
    const emitDescriptionSaveRef = useRef(() => {})
    const appLinkRangeRef = useRef(null)
    const openAppLinkPickerRef = useRef(null)
    const [activeFormats, setActiveFormats] = useState({})
    const [isFocused, setIsFocused] = useState(false)
    const [appLinkPickerOpen, setAppLinkPickerOpen] = useState(false)
    const [appLinkItems, setAppLinkItems] = useState(() =>
        Array.isArray(linkCatalog) ? linkCatalog : []
    )
    const [appLinkLoading, setAppLinkLoading] = useState(false)

    const imageInputRef = useRef(null)
    const openMediaViewerRef = useRef(null)
    const [mediaViewer, setMediaViewer] = useState({
        open: false,
        files: [],
        index: 0,
    })

    openMediaViewerRef.current = (files, index = 0) => {
        setMediaViewer({
            open: true,
            files: Array.isArray(files) ? files : [],
            index,
        })
    }

    const insertSections = useMemo(
        () =>
            buildDescriptionInsertSections({
                enableMentions,
                enableAppLinks,
            }),
        [enableMentions, enableAppLinks]
    )

    // ── Mention refs (mirrors MentionEditor.js) ─────────────────────────────
    const projectIdRef = useRef(projectId)
    const userProfileRef = useRef(userProfile)
    const mentionUsersRef = useRef([])
    const mentionHasNextRef = useRef(false)
    const mentionSearchRef = useRef('')
    const mentionFetchLoadingRef = useRef(false)
    const mentionLoadMoreRef = useRef(false)
    const mentionRequestIdRef = useRef(0)
    const mentionListScrollRef = useRef(null)

    // Keep refs in sync with latest prop / selector values
    useEffect(() => {
        projectIdRef.current = projectId
    }, [projectId])
    useEffect(() => {
        userProfileRef.current = userProfile
    }, [userProfile])
    useEffect(() => {
        onSaveRef.current = onSave
    })
    useEffect(() => {
        readOnlyRef.current = readOnly
    }, [readOnly])
    useEffect(() => {
        enableMentionsRef.current = enableMentions
    }, [enableMentions])
    useEffect(() => {
        mentionListRef.current =
            mentionList === 'tasks' || mentionList === 'main'
                ? mentionList
                : 'support'
    }, [mentionList])
    useEffect(() => {
        enableAppLinksRef.current = enableAppLinks
    }, [enableAppLinks])
    useEffect(() => {
        valueRef.current = value
    }, [value])
    useEffect(() => {
        isFocusedRef.current = isFocused
    }, [isFocused])

    emitDescriptionSaveRef.current = (quill, { requireUserEdit = true } = {}) => {
        if (!quill || readOnlyRef.current) return
        const html = normalizeHtml(quill.root.innerHTML)
        if (
            descriptionsAreEqual(html, savedRef.current) ||
            descriptionsAreEqual(html, valueRef.current)
        ) {
            savedRef.current = html
            return
        }
        // Ignore teardown/remount noise — only persist real user edits.
        if (requireUserEdit && !hasUserEditedRef.current) {
            savedRef.current = html
            return
        }
        hasUserEditedRef.current = false
        savedRef.current = html
        if (enableMentionsRef.current) {
            onSaveRef.current?.(extractDescriptionMentionState(quill, html))
            return
        }
        onSaveRef.current?.(html)
    }
    useEffect(() => {
        linkCatalogRef.current = linkCatalog
        if (Array.isArray(linkCatalog)) {
            setAppLinkItems(linkCatalog)
        }
    }, [linkCatalog])

    useEffect(() => {
        appLinkItemsRef.current = appLinkItems
    }, [appLinkItems])

    const closeAppLinkPicker = useCallback(() => {
        setAppLinkPickerOpen(false)
        appLinkRangeRef.current = null
        quillRef.current?.focus()
    }, [])

    const openAppLinkPicker = useCallback(async () => {
        if (readOnlyRef.current || !enableAppLinksRef.current) return
        const q = quillRef.current
        if (!q) return

        const range = q.getSelection(true) ?? {
            index: Math.max(q.getLength() - 1, 0),
            length: 0,
        }
        appLinkRangeRef.current = { index: range.index, length: range.length }
        setAppLinkPickerOpen(true)
        setIsFocused(true)

        const provided = linkCatalogRef.current
        if (Array.isArray(provided) && provided.length > 0) {
            setAppLinkItems(provided)
            setAppLinkLoading(false)
            return
        }

        if (!projectIdRef.current) {
            setAppLinkItems([])
            setAppLinkLoading(false)
            return
        }

        setAppLinkLoading(true)
        try {
            const items = await loadProjectStoryMapLinkCatalog(
                projectIdRef.current
            )
            if (!isUnmountingRef.current) {
                setAppLinkItems(items)
            }
        } finally {
            if (!isUnmountingRef.current) {
                setAppLinkLoading(false)
            }
        }
    }, [])

    useEffect(() => {
        openAppLinkPickerRef.current = openAppLinkPicker
    }, [openAppLinkPicker])

    const handleAppLinkSelect = useCallback(
        (item) => {
            const q = quillRef.current
            if (!q || !item?.href) {
                closeAppLinkPicker()
                return
            }

            const saved = appLinkRangeRef.current
            const index = saved?.index ?? Math.max(q.getLength() - 1, 0)
            const length = saved?.length ?? 0
            if (length > 0) {
                q.deleteText(index, length, 'user')
            }

            const label = formatAppLinkLabel(item.kind, item.title)
            const tooltip = formatAppLinkTooltip(
                item.kind,
                item.title,
                item.crumbs
            )
            q.insertText(
                index,
                label,
                toAppLinkFormats(item.href, tooltip),
                'user'
            )
            q.setSelection(index + label.length, 0, 'silent')
            closeAppLinkPicker()
        },
        [closeAppLinkPicker]
    )

    // ── Link popup ──────────────────────────────────────────────────────────
    const [linkPopup, setLinkPopup] = useState({
        open: false,
        text: '',
        url: '',
        savedRange: null,
    })
    const linkGroupRef = useRef(null)
    const linkUrlInputRef = useRef(null)
    const linkTextInputRef = useRef(null)

    // ── Emoji picker ────────────────────────────────────────────────────────
    const [emojiOpen, setEmojiOpen] = useState(false)
    const savedEmojiRange = useRef(null)

    // ── Quill init ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!mountRef.current || quillRef.current) return

        isUnmountingRef.current = false

        const quill = new Quill(mountRef.current, {
            theme: 'snow',
            placeholder,
            modules: {
                // toolbar: false keeps Quill away from the toolbar DOM,
                // eliminating the React/Quill removeChild conflict.
                toolbar: false,
                history: { delay: 1000, maxStack: 100, userOnly: true },
                mention: {
                    mentionDenotationChars: enableMentionsRef.current
                        ? ['@']
                        : [],
                    isolateCharacter: true,
                    minChars: 0,
                    maxChars: 50,
                    allowedChars: /^[a-zA-Z0-9_\s.'@+-]*$/,
                    positioningStrategy: 'fixed',
                    defaultMenuOrientation: 'bottom',
                    mentionContainerClass: `ql-mention-list-container ${assigneeStyles.assigneeSelectorPopup}`,
                    mentionListClass: `ql-mention-list ${assigneeStyles.assigneeSelectorList}`,
                    listItemClass: 'ql-mention-list-item',
                    renderItem: renderMentionItem,
                    renderLoading: () => {
                        const el = document.createElement('div')
                        el.className = assigneeStyles.assigneeSelectorLoading
                        el.innerHTML =
                            '<span style="display:inline-flex;color:#6366f1"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2a10 10 0 1 0 10 10" opacity="0.3"/><path fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-2a8 8 0 0 0-8-8z"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></path></svg></span>'
                        return el
                    },
                    onOpen: () => {
                        setTimeout(() => attachMentionListScroll(), 0)
                    },
                    onBeforeClose: () => {
                        detachMentionListScroll()
                    },
                    source: (searchTerm, renderList) => {
                        if (!enableMentionsRef.current) {
                            renderList([], searchTerm)
                            return
                        }
                        const query = (searchTerm ?? '').trim()
                        if (!query) {
                            renderList(
                                [
                                    {
                                        id: MENTION_HINT_ID,
                                        value: '',
                                        disabled: true,
                                    },
                                ],
                                searchTerm
                            )
                            return
                        }
                        if (projectIdRef.current) {
                            mentionRequestIdRef.current += 1
                            debouncedRemoteSearchRef.current.cancel()
                            debouncedRemoteSearchRef.current(
                                searchTerm,
                                renderList
                            )
                        }
                    },
                },
            },
        })

        quillRef.current = quill

        if (value) {
            const html =
                typeof value === 'string' ? toQuillHtml(value) || value : ''
            quill.clipboard.dangerouslyPasteHTML(html)
        }
        quill.setSelection(null, 'silent')
        hasUserEditedRef.current = false
        savedRef.current = normalizeHtml(quill.root.innerHTML)
        if (readOnlyRef.current) {
            quill.enable(false)
        }

        const syncFormats = () => {
            const range = quill.getSelection()
            if (range) setActiveFormats(quill.getFormat(range))
        }

        quill.on('selection-change', syncFormats)
        quill.on('text-change', (delta, oldDelta, source) => {
            if (source === 'user') {
                hasUserEditedRef.current = true
            }
            syncFormats()
        })

        const handleBlur = (e) => {
            if (isUnmountingRef.current || readOnlyRef.current) return
            if (wrapperRef.current?.contains(e.relatedTarget)) return

            emitDescriptionSaveRef.current(quill)
        }

        quill.root.addEventListener('blur', handleBlur)

        // quill-mention portals its list to document.body, so it never sees
        // clicks outside the editor or the drawer closing.
        const handleOutsidePointer = (e) => {
            const container = quill.getModule('mention')?.mentionContainer
            if (!container?.isConnected) return
            const target = e.target
            if (quill.root.contains(target) || container.contains(target)) {
                return
            }
            closeMentionListRef.current()
        }
        document.addEventListener('mousedown', handleOutsidePointer, true)

        const handlePaste = (e) => {
            if (isUnmountingRef.current || readOnlyRef.current) return

            const plain = e.clipboardData?.getData('text/plain') || ''
            const clipboardHtml = e.clipboardData?.getData('text/html') || ''

            if (shouldConvertPastedMarkdown(plain, clipboardHtml)) {
                const converted = markdownToHtml(plain)
                if (!converted) return

                e.preventDefault()
                e.stopPropagation()

                const mdRange = quill.getSelection(true) ?? {
                    index: Math.max(quill.getLength() - 1, 0),
                    length: 0,
                }
                if (mdRange.length) {
                    quill.deleteText(mdRange.index, mdRange.length, 'user')
                }
                quill.clipboard.dangerouslyPasteHTML(
                    mdRange.index,
                    converted,
                    'user'
                )
                hasUserEditedRef.current = true
                return
            }

            if (!shouldResolveAppLinkPaste(plain)) return

            e.preventDefault()
            e.stopPropagation()

            const pasted = plain.trim()
            let href
            try {
                href = new URL(pasted, window.location.origin).href
            } catch {
                href = pasted
            }

            const range = quill.getSelection(true) ?? {
                index: Math.max(quill.getLength() - 1, 0),
                length: 0,
            }
            if (range.length) {
                quill.deleteText(range.index, range.length, 'user')
            }

            const insertIndex = range.index
            // Insert URL as linked text first; swap to title when resolve succeeds.
            quill.insertText(insertIndex, pasted, 'link', href, 'user')
            quill.setSelection(insertIndex + pasted.length, 0, 'silent')

            resolveAppLinkTitle(pasted).then((resolved) => {
                if (isUnmountingRef.current || !resolved?.title) return
                const q = quillRef.current
                if (!q || q !== quill) return

                const current = q.getText(insertIndex, pasted.length)
                if (current !== pasted) return

                const resolvedHref = resolved.href || href
                const catalogItem = findCatalogItemForLink(
                    appLinkItemsRef.current.length
                        ? appLinkItemsRef.current
                        : linkCatalogRef.current,
                    resolvedHref,
                    resolved.kind
                )
                const tooltip = formatAppLinkTooltip(
                    resolved.kind,
                    resolved.title,
                    catalogItem?.crumbs
                )

                q.deleteText(insertIndex, pasted.length, 'user')
                q.insertText(
                    insertIndex,
                    resolved.title,
                    toAppLinkFormats(resolvedHref, tooltip),
                    'user'
                )
                q.setSelection(
                    insertIndex + resolved.title.length,
                    0,
                    'silent'
                )
            })
        }

        quill.root.addEventListener('paste', handlePaste, true)

        const handleLinkClick = (e) => {
            const anchor = e.target?.closest?.('a[href]')
            if (!anchor || !quill.root.contains(anchor)) return

            const href = anchor.getAttribute('href')
            if (!href || href === '#') return

            e.preventDefault()
            e.stopPropagation()
            window.open(href, '_blank', 'noopener,noreferrer')
        }

        quill.root.addEventListener('click', handleLinkClick)

        const handleImageClick = (e) => {
            const img = e.target?.closest?.('img')
            if (!img || !quill.root.contains(img)) return
            // Ignore mention avatars and other non-content images
            if (img.closest('.mention, .ql-mention-list-item')) return

            const src = img.getAttribute('src')
            if (!src) return

            e.preventDefault()
            e.stopPropagation()

            const images = Array.from(
                quill.root.querySelectorAll('img')
            ).filter((el) => !el.closest('.mention, .ql-mention-list-item'))
            const files = images
                .map((el) => el.getAttribute('src'))
                .filter(Boolean)
            const index = Math.max(0, files.indexOf(src))
            openMediaViewerRef.current?.(files, index)
        }

        quill.root.addEventListener('click', handleImageClick)

        const handleHashShortcut = (e) => {
            if (isUnmountingRef.current || readOnlyRef.current) return
            if (!enableAppLinksRef.current) return
            if (e.key !== '#') return
            if (e.ctrlKey || e.metaKey || e.altKey) return
            e.preventDefault()
            openAppLinkPickerRef.current?.()
        }

        quill.root.addEventListener('keydown', handleHashShortcut)

        return () => {
            isUnmountingRef.current = true
            closeMentionListRef.current()
            document.removeEventListener('mousedown', handleOutsidePointer, true)
            quill.off('selection-change', syncFormats)
            quill.off('text-change', syncFormats)
            quill.root.removeEventListener('blur', handleBlur)
            quill.root.removeEventListener('paste', handlePaste, true)
            quill.root.removeEventListener('click', handleLinkClick)
            quill.root.removeEventListener('click', handleImageClick)
            quill.root.removeEventListener('keydown', handleHashShortcut)
            quillRef.current = null
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const quill = quillRef.current
        if (!quill || isFocusedRef.current) return

        const next = normalizeHtml(toQuillHtml(value) || value)
        const current = normalizeHtml(quill.root.innerHTML)
        if (next === current || descriptionsAreEqual(next, current)) return

        quill.clipboard.dangerouslyPasteHTML(next || EMPTY_HTML)
        hasUserEditedRef.current = false
        savedRef.current = next
        quill.setSelection(null, 'silent')
    }, [value])

    useEffect(() => {
        const quill = quillRef.current
        if (!quill) return
        quill.enable(!readOnly)
    }, [readOnly])

    // ── Format handlers ─────────────────────────────────────────────────────
    const applyFormat = useCallback(
        (format, value) => (e) => {
            e.preventDefault()
            const q = quillRef.current
            if (!q) return
            q.focus()
            const current = q.getFormat()
            if (value !== undefined) {
                // toggle: same value → remove, different → apply
                q.format(format, current[format] === value ? false : value)
            } else {
                q.format(format, !current[format])
            }
            const range = q.getSelection()
            if (range) setActiveFormats(q.getFormat(range))
        },
        []
    )

    const handleHeader = useCallback((e) => {
        const q = quillRef.current
        if (!q) return
        q.focus()
        const val = e.target.value
        q.format('header', val ? Number(val) : false)
        const range = q.getSelection()
        if (range) setActiveFormats(q.getFormat(range))
    }, [])

    // Resolve selection to full link span when caret sits inside a link.
    const resolveLinkEditRange = useCallback((q, range) => {
        if (!q || !range) {
            return { range: null, text: '', url: '' }
        }

        const fmt = q.getFormat(range) || {}
        let index = range.index
        let length = range.length
        let url = getLinkHref(fmt.link)
        let text = length > 0 ? q.getText(index, length) : ''

        // Prefer the full link span so edit popup gets title + URL together
        if (url || fmt.link) {
            try {
                const leafAt = Math.max(index, 0)
                const [leaf] = q.getLeaf(leafAt)
                let blot = leaf
                while (blot && blot.statics?.blotName !== 'link') {
                    blot = blot.parent
                }
                if (blot && typeof q.getIndex === 'function') {
                    index = q.getIndex(blot)
                    length = blot.length?.() ?? 0
                    // Quill link blot trailing newline edge — exclude if present
                    const raw = length > 0 ? q.getText(index, length) : ''
                    text = raw.replace(/\n$/, '')
                    length = text.length
                    const linkFmt = blot.formats?.() || {}
                    url = getLinkHref(linkFmt.link) || url
                    if (!url && blot.domNode?.getAttribute) {
                        url = blot.domNode.getAttribute('href') || url
                    }
                } else if (url) {
                    // Fallback: expand while neighboring chars share the same link
                    let start = index
                    let end = index + Math.max(length, 0)
                    while (start > 0) {
                        const prev = q.getFormat(start - 1, 1)
                        if (!linksShareHref(prev?.link, url)) break
                        start -= 1
                    }
                    const docLen = q.getLength()
                    while (end < docLen - 1) {
                        const next = q.getFormat(end, 1)
                        if (!linksShareHref(next?.link, url)) break
                        end += 1
                    }
                    index = start
                    length = Math.max(end - start, 0)
                    text =
                        length > 0
                            ? q.getText(index, length).replace(/\n$/, '')
                            : ''
                    length = text.length
                }
            } catch {
                // keep current range
            }
        }

        return {
            range: { index, length },
            text,
            url,
        }
    }, [])

    // Open the inline link popup — called on mousedown so editor still has focus
    const handleLinkOpen = useCallback(
        (e) => {
            e.preventDefault()
            const q = quillRef.current
            if (!q) return

            const selection = q.getSelection() || {
                index: Math.max(q.getLength() - 1, 0),
                length: 0,
            }
            const resolved = resolveLinkEditRange(q, selection)

            setLinkPopup({
                open: true,
                text: resolved.text || '',
                url: getLinkHref(resolved.url) || '',
                savedRange: resolved.range || selection,
            })
        },
        [resolveLinkEditRange]
    )

    const closeLinkPopup = useCallback(() => {
        setLinkPopup({ open: false, text: '', url: '', savedRange: null })
        quillRef.current?.focus()
    }, [])

    // ── Emoji picker handlers ───────────────────────────────────────────────
    const closeEmojiPicker = useCallback(() => {
        setEmojiOpen(false)
        quillRef.current?.focus()
    }, [])

    const handleEmojiSelect = useCallback((emoji) => {
        const q = quillRef.current
        if (!q) return
        q.focus()
        const range = savedEmojiRange.current ??
            q.getSelection(true) ?? { index: q.getLength(), length: 0 }
        const idx = range.index + range.length
        q.insertText(idx, emoji.native, 'user')
        q.setSelection(idx + emoji.native.length, 0)
        setEmojiOpen(false)
    }, [])

    const handleLinkSubmit = useCallback(() => {
        const q = quillRef.current
        if (!q) return

        const { text, url, savedRange } = linkPopup
        const href = url.trim()
        const label = text.trim() || href

        if (!href) {
            // No URL — if there's an existing link on selection, remove it
            if (savedRange?.length) {
                q.setSelection(savedRange.index, savedRange.length, 'silent')
                q.format('link', false)
            }
            closeLinkPopup()
            return
        }

        const parsed = parseAppDeepLink(href)
        const kind =
            parsed?.type === 'task'
                ? 'task'
                : parsed?.type === 'storyMapBoard'
                  ? 'storyMap'
                  : parsed?.cardType || null
        const catalogItem = kind
            ? findCatalogItemForLink(
                  appLinkItemsRef.current.length
                      ? appLinkItemsRef.current
                      : linkCatalogRef.current,
                  href,
                  kind
              )
            : null
        const tooltip = kind
            ? formatAppLinkTooltip(
                  kind,
                  label,
                  catalogItem?.crumbs
              )
            : ''
        const formats = toAppLinkFormats(href, tooltip)
        const linkFormats =
            Object.keys(formats).length > 0 ? formats : { link: href }

        if (savedRange?.length) {
            // Replace selected/linked span so title edits apply too
            q.deleteText(savedRange.index, savedRange.length, 'user')
            q.insertText(
                savedRange.index,
                label,
                linkFormats,
                'user'
            )
            q.setSelection(savedRange.index + label.length, 0, 'silent')
        } else {
            const idx = savedRange?.index ?? Math.max(q.getLength() - 1, 0)
            q.insertText(idx, label, linkFormats, 'user')
            q.setSelection(idx + label.length, 0, 'silent')
        }

        setActiveFormats(
            q.getFormat(q.getSelection() ?? { index: 0, length: 0 })
        )
        closeLinkPopup()
    }, [linkPopup, closeLinkPopup])

    const handleLinkKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                handleLinkSubmit()
            }
            if (e.key === 'Escape') {
                e.preventDefault()
                closeLinkPopup()
            }
        },
        [handleLinkSubmit, closeLinkPopup]
    )

    // Auto-focus the right input when popup opens
    useLayoutEffect(() => {
        if (!linkPopup.open) return
        // If text came from selection, jump straight to URL; otherwise start with text
        const focusTarget = linkPopup.text
            ? linkUrlInputRef.current
            : linkTextInputRef.current
        setTimeout(() => focusTarget?.focus(), 0)
    }, [linkPopup.open]) // eslint-disable-line react-hooks/exhaustive-deps

    // Close popup on outside click
    useEffect(() => {
        if (!linkPopup.open) return
        const handleOutside = (e) => {
            if (
                linkGroupRef.current &&
                !linkGroupRef.current.contains(e.target)
            ) {
                closeLinkPopup()
            }
        }
        document.addEventListener('mousedown', handleOutside)
        return () => document.removeEventListener('mousedown', handleOutside)
    }, [linkPopup.open, closeLinkPopup])

    const handleClean = useCallback((e) => {
        e.preventDefault()
        const q = quillRef.current
        if (!q) return
        q.focus()
        const range = q.getSelection()
        if (range) {
            q.removeFormat(range.index, range.length || q.getLength())
        }
        setActiveFormats({})
    }, [])

    const handleUndo = useCallback((e) => {
        e.preventDefault()
        quillRef.current?.history?.undo()
    }, [])

    const handleRedo = useCallback((e) => {
        e.preventDefault()
        quillRef.current?.history?.redo()
    }, [])

    // ── Mention helpers (mirrors MentionEditor.js) ──────────────────────────
    const filterMentionUsers = useCallback((users) => {
        const exclude = new Set([userProfileRef.current?._id].filter(Boolean))
        return (users || []).filter((u) => u?.id && !exclude.has(u.id))
    }, [])

    const fetchMentionUsers = useCallback(
        async (search, skip, append) => {
            const pid = projectIdRef.current
            if (!pid) return []
            if (!append) mentionFetchLoadingRef.current = true
            try {
                const kind = mentionListRef.current
                const result = await userListForClient({
                    skip,
                    limit: MENTION_PAGE_LIMIT,
                    search: search || '',
                    filter: {},
                    projectId: pid,
                    ...(kind === 'main' || kind === 'tasks'
                        ? { type: 'pms' }
                        : {}),
                })

                const mapped = filterMentionUsers(
                    (result?.data || []).map((u) => ({
                        id: u?._id,
                        value: u?.name || u?.display || '',
                        avatar: u?.profilePicture,
                        email: u?.email,
                    }))
                )

                if (append) {
                    const seen = new Set(
                        mentionUsersRef.current.map((u) => u.id)
                    )
                    mentionUsersRef.current = [
                        ...mentionUsersRef.current,
                        ...mapped.filter((u) => !seen.has(u.id)),
                    ]
                } else {
                    mentionUsersRef.current = mapped
                }
                mentionHasNextRef.current = Boolean(result?.isNext)
                mentionSearchRef.current = search
                return mentionUsersRef.current
            } catch {
                if (!append) mentionUsersRef.current = []
                mentionHasNextRef.current = false
                return mentionUsersRef.current
            } finally {
                if (!append) mentionFetchLoadingRef.current = false
            }
        },
        [filterMentionUsers]
    )

    const getMentionListForRender = useCallback(() => {
        const items = [...mentionUsersRef.current]
        if (mentionHasNextRef.current && mentionLoadMoreRef.current) {
            items.push({
                id: MENTION_LOADING_MORE_ID,
                value: '',
                disabled: true,
            })
        }
        return items
    }, [])

    const refreshMentionDropdown = useCallback(() => {
        const mentionMod = quillRef.current?.getModule('mention')
        if (!mentionMod || !mentionSearchRef.current) return
        mentionMod.renderList(
            '@',
            getMentionListForRender(),
            mentionSearchRef.current
        )
    }, [getMentionListForRender])

    const handleMentionListScroll = useCallback(
        async (e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target
            if (scrollHeight - scrollTop > clientHeight + 40) return
            if (!projectIdRef.current || !mentionHasNextRef.current) return
            if (mentionLoadMoreRef.current || mentionFetchLoadingRef.current)
                return

            mentionLoadMoreRef.current = true
            refreshMentionDropdown()
            const prevLen = mentionUsersRef.current.length
            try {
                await fetchMentionUsers(mentionSearchRef.current, prevLen, true)
            } finally {
                mentionLoadMoreRef.current = false
                refreshMentionDropdown()
            }
        },
        [fetchMentionUsers, refreshMentionDropdown]
    )

    const attachMentionListScroll = useCallback(() => {
        const list =
            document.querySelector(`.${assigneeStyles.assigneeSelectorList}`) ||
            document.querySelector('.ql-mention-list')
        if (!list || mentionListScrollRef.current === list) return
        if (mentionListScrollRef.current) {
            mentionListScrollRef.current.removeEventListener(
                'scroll',
                handleMentionListScroll
            )
        }
        list.addEventListener('scroll', handleMentionListScroll)
        mentionListScrollRef.current = list
    }, [handleMentionListScroll])

    const detachMentionListScroll = useCallback(() => {
        if (mentionListScrollRef.current) {
            mentionListScrollRef.current.removeEventListener(
                'scroll',
                handleMentionListScroll
            )
            mentionListScrollRef.current = null
        }
    }, [handleMentionListScroll])

    const renderMentionItem = useCallback((item) => {
        if (item?.id === MENTION_HINT_ID) {
            const hint = document.createElement('div')
            hint.className = 'mention-hint-item'
            hint.textContent =
                'Mention a teammate or link to a task, project, or message.'
            return hint
        }
        if (item?.id === '__mention_empty__') {
            const empty = document.createElement('div')
            empty.className = 'mention-hint-item'
            empty.textContent = 'No matching people'
            return empty
        }
        if (item?.id === MENTION_LOADING_MORE_ID) {
            const loading = document.createElement('div')
            loading.className = assigneeStyles.assigneeSelectorLoadingMore
            loading.innerHTML =
                '<span style="display:inline-flex;color:#6366f1"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2a10 10 0 1 0 10 10" opacity="0.3"/><path fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-2a8 8 0 0 0-8-8z"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></path></svg></span>'
            return loading
        }

        const container = document.createElement('div')
        container.className = assigneeStyles.assigneeSelectorItem

        const avatarWrapper = document.createElement('span')
        avatarWrapper.style.cssText = [
            'width:26px',
            'height:26px',
            'min-width:26px',
            'max-width:26px',
            'border-radius:50%',
            'overflow:hidden',
            'display:inline-flex',
            'align-items:center',
            'justify-content:center',
            'flex-shrink:0',
            'font-size:14px',
            'font-weight:600',
            'color:#ffffff',
            `background-color:${getMentionAvatarColor(item?.value)}`,
        ].join(';')

        if (item?.avatar) {
            const img = document.createElement('img')
            img.src = item.avatar
            img.alt = item?.value || ''
            img.style.cssText =
                'width:26px;height:26px;object-fit:cover;display:block;'
            avatarWrapper.appendChild(img)
        } else {
            avatarWrapper.textContent = getMentionInitials(item?.value)
        }

        const info = document.createElement('div')
        info.className = assigneeStyles.assigneeSelectorItemInfo
        info.style.flexDirection = 'column'
        info.style.alignItems = 'flex-start'
        info.style.gap = '2px'

        const name = document.createElement('span')
        name.className = assigneeStyles.assigneeSelectorName
        name.textContent = item?.value || ''
        info.appendChild(name)

        if (item?.email) {
            const email = document.createElement('span')
            email.className = assigneeStyles.assigneeSelectorEmail
            email.textContent = item.email
            info.appendChild(email)
        }

        container.appendChild(avatarWrapper)
        container.appendChild(info)
        return container
    }, [])

    fetchMentionUsersRef.current = fetchMentionUsers
    getMentionListForRenderRef.current = getMentionListForRender

    const debouncedRemoteSearchRef = useRef(
        debounce(async (searchTerm, renderList) => {
            const query = (searchTerm ?? '').trim()
            if (!query || !projectIdRef.current) {
                renderList([], searchTerm)
                return
            }
            const requestId = ++mentionRequestIdRef.current
            await fetchMentionUsersRef.current(query, 0, false)
            if (requestId !== mentionRequestIdRef.current) return
            const items = getMentionListForRenderRef.current()
            renderList(
                items.length
                    ? items
                    : [
                          {
                              id: '__mention_empty__',
                              value: 'No matching people',
                              disabled: true,
                          },
                      ],
                searchTerm
            )
        }, 300)
    )

    closeMentionListRef.current = () => {
        mentionRequestIdRef.current += 1
        debouncedRemoteSearchRef.current?.cancel()
        const mentionMod = quillRef.current?.getModule('mention')
        if (!mentionMod) return
        if (mentionMod.existingSourceExecutionToken) {
            mentionMod.existingSourceExecutionToken.abandoned = true
            mentionMod.existingSourceExecutionToken = undefined
        }
        if (mentionMod.isOpen || mentionMod.mentionContainer?.isConnected) {
            mentionMod.hideMentionList()
        }
    }

    useEffect(() => {
        if (!isActive) closeMentionListRef.current()
    }, [isActive])

    // Cleanup debounce + scroll listener on unmount
    useEffect(() => {
        const debouncedSearch = debouncedRemoteSearchRef.current
        return () => {
            debouncedSearch?.cancel()
            detachMentionListScroll()
        }
    }, [detachMentionListScroll])

    const handleInsert = useCallback(
        (key) => {
            const q = quillRef.current
            if (!q) return
            q.focus()
            const range = q.getSelection(true) ?? {
                index: q.getLength(),
                length: 0,
            }

            switch (key) {
                case 'paragraph':
                    q.removeFormat(range.index, Math.max(range.length, 1))
                    break
                case 'header1':
                    q.format('header', 1)
                    break
                case 'header2':
                    q.format('header', 2)
                    break
                case 'bullet':
                    q.format('list', 'bullet')
                    break
                case 'ordered':
                    q.format('list', 'ordered')
                    break
                case 'quote': {
                    const fmt = q.getFormat(range)
                    q.format('blockquote', !fmt.blockquote)
                    break
                }
                case 'code':
                    q.format('code-block', true)
                    break
                case 'sectionBreak': {
                    const idx = range.index + range.length
                    q.insertEmbed(idx, 'divider', true)
                    q.setSelection(idx + 1)
                    break
                }
                case 'image':
                    imageInputRef.current?.click()
                    return false
                case 'mention': {
                    if (!enableMentionsRef.current) return false
                    // Insert '@' — quill-mention auto-detects it and opens the dropdown
                    const idx = range.index + range.length
                    q.insertText(idx, '@', 'user')
                    q.setSelection(idx + 1, 0, 'user')
                    break
                }
                case 'appLink':
                    if (!enableAppLinksRef.current) return false
                    openAppLinkPickerRef.current?.()
                    return false
                case 'link':
                    handleLinkOpen({ preventDefault: () => {} })
                    return false
                case 'table':
                    q.clipboard.dangerouslyPasteHTML(
                        range.index,
                        '<table><tbody>' +
                            '<tr><td><br></td><td><br></td></tr>' +
                            '<tr><td><br></td><td><br></td></tr>' +
                            '</tbody></table>'
                    )
                    break
                case 'emoji':
                    savedEmojiRange.current = q.getSelection(true)
                    setEmojiOpen(true)
                    // Close insert menu; emoji popover stays open
                    break
                default:
                    break
            }
        },
        [handleLinkOpen]
    )

    // Handle image file selection → embed as base64 and persist description
    const handleImageFile = useCallback((e) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file) return
        const isImage =
            file.type?.startsWith('image/') ||
            /\.(png|jpe?g|gif|webp|svg|bmp|ico)$/i.test(file.name || '')
        if (!isImage) {
            message.warning('Please select an image file only')
            return
        }
        const reader = new FileReader()
        reader.onload = (ev) => {
            const q = quillRef.current
            if (!q || readOnlyRef.current) return
            const range = q.getSelection(true) ?? { index: q.getLength() }
            q.insertEmbed(range.index, 'image', ev.target.result)
            q.setSelection(range.index + 1)

            hasUserEditedRef.current = true
            emitDescriptionSaveRef.current(q, { requireUserEdit: false })
        }
        reader.readAsDataURL(file)
    }, [])

    // ── Active state helper ──────────────────────────────────────────────────
    const btn = (format, val) => {
        const isActive =
            val !== undefined
                ? activeFormats[format] === val
                : !!activeFormats[format]
        return `${styles.toolbarBtn} ${isActive ? styles.toolbarBtnActive : ''}`
    }

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div
            ref={wrapperRef}
            className={`${styles.wrapper} ${readOnly ? styles.wrapperReadOnly : ''}`}
            onFocus={() => {
                if (readOnly) return
                setIsFocused(true)
            }}
            onBlur={(e) => {
                if (readOnly) return
                // Only hide toolbar when focus leaves the entire component
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    if (appLinkPickerOpen) return
                    setIsFocused(false)
                }
            }}
        >
            {/* Editor — React renders this empty; Quill owns its children */}
            <div ref={mountRef} />

            {/* Toolbar — visible only while wrapper is focused (or emoji picker is open) */}
            {!readOnly ? (
            <div
                className={`${styles.toolbar} ${
                    isFocused || emojiOpen ? styles.toolbarVisible : ''
                }`}
            >
                {/* ── + Insert ──────────────────────────────────────────── */}
                <QuillInsertDropdown
                    sections={insertSections}
                    onInsertItem={handleInsert}
                    plusButtonClassName={`${styles.toolbarBtn} ${styles.plusBtn}`}
                    wrapClassName={`${styles.toolbarGroup} ${styles.insertGroup}`}
                    buttonTitle="Insert block"
                    showLinkPopover={false}
                    emoji={{
                        open: emojiOpen,
                        onOpenChange: (open) => {
                            if (open) setEmojiOpen(true)
                            else closeEmojiPicker()
                        },
                        onSelect: handleEmojiSelect,
                        savedRangeRef: savedEmojiRange,
                    }}
                    image={{
                        inputRef: imageInputRef,
                        onChange: handleImageFile,
                    }}
                />

                {/* Separator between + and Undo */}
                <span className={styles.toolbarSeparator} />

                {/* Undo / Redo */}
                <span className={styles.toolbarGroup}>
                    <button
                        className={styles.toolbarBtn}
                        onMouseDown={handleUndo}
                        title="Undo (Ctrl+Z)"
                        type="button"
                    >
                        <Icon
                            icon="iconoir:undo"
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                        />
                    </button>
                    <button
                        className={styles.toolbarBtn}
                        onMouseDown={handleRedo}
                        title="Redo (Ctrl+Y)"
                        type="button"
                    >
                        <Icon
                            icon="iconoir:redo"
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                        />
                    </button>
                </span>

                {/* Text formatting */}
                <span className={styles.toolbarGroup}>
                    <button
                        className={btn('bold')}
                        onMouseDown={applyFormat('bold')}
                        title="Bold (Ctrl+B)"
                        type="button"
                    >
                        <Icon
                            icon="teenyicons:bold-solid"
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                        />
                    </button>
                    <button
                        className={btn('italic')}
                        onMouseDown={applyFormat('italic')}
                        title="Italic (Ctrl+I)"
                        type="button"
                    >
                        <Icon
                            icon="si:italic-line"
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                        />
                    </button>
                    <button
                        className={btn('underline')}
                        onMouseDown={applyFormat('underline')}
                        title="Underline (Ctrl+U)"
                        type="button"
                    >
                        <Icon
                            icon="solar:text-underline-linear"
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                        />
                    </button>
                    <button
                        className={btn('strike')}
                        onMouseDown={applyFormat('strike')}
                        title="Strikethrough"
                        type="button"
                    >
                        <Icon
                            icon="heroicons:strikethrough-solid"
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                        />
                    </button>
                </span>

                {/* Lists */}
                <span className={styles.toolbarGroup}>
                    <button
                        className={btn('list', 'ordered')}
                        onMouseDown={applyFormat('list', 'ordered')}
                        title="Numbered list"
                        type="button"
                    >
                        <Icon
                            icon="pajamas:list-numbered"
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                        />
                    </button>
                    <button
                        className={btn('list', 'bullet')}
                        onMouseDown={applyFormat('list', 'bullet')}
                        title="Bullet list"
                        type="button"
                    >
                        <Icon
                            icon="proicons:bullet-list"
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                        />
                    </button>
                </span>

                {/* Block formats */}
                <span className={styles.toolbarGroup}>
                    <button
                        className={btn('blockquote')}
                        onMouseDown={applyFormat('blockquote')}
                        title="Blockquote"
                        type="button"
                    >
                        <Icon
                            icon="sidekickicons:blockquote"
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                        />
                    </button>
                    <button
                        className={btn('code-block')}
                        onMouseDown={applyFormat('code-block')}
                        title="Code block"
                        type="button"
                    >
                        <Icon
                            icon="material-symbols:code"
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                        />
                    </button>
                </span>

                {/* Heading select — plain React <select>, no Quill involvement */}
                <span className={styles.toolbarGroup}>
                    <select
                        className={styles.headingSelect}
                        value={String(activeFormats.header || '')}
                        onChange={handleHeader}
                        title="Heading"
                    >
                        <option value="">Normal</option>
                        <option value="1">H1</option>
                        <option value="2">H2</option>
                        <option value="3">H3</option>
                    </select>
                </span>

                {/* Link */}
                <span
                    ref={linkGroupRef}
                    className={`${styles.toolbarGroup} ${styles.linkGroup}`}
                >
                    <button
                        className={btn('link')}
                        onMouseDown={handleLinkOpen}
                        title="Insert link"
                        type="button"
                    >
                        <Icon
                            icon="ph:link"
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                        />
                    </button>

                    {linkPopup.open && (
                        <div
                            className={styles.linkPopup}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <input
                                ref={linkTextInputRef}
                                className={styles.linkInput}
                                value={linkPopup.text}
                                onChange={(e) =>
                                    setLinkPopup((s) => ({
                                        ...s,
                                        text: e.target.value,
                                    }))
                                }
                                onKeyDown={handleLinkKeyDown}
                                placeholder="Add text"
                                type="text"
                            />
                            <input
                                ref={linkUrlInputRef}
                                className={styles.linkInput}
                                value={linkPopup.url}
                                onChange={(e) =>
                                    setLinkPopup((s) => ({
                                        ...s,
                                        url: e.target.value,
                                    }))
                                }
                                onKeyDown={handleLinkKeyDown}
                                placeholder="Add URL"
                                type="url"
                            />
                            <div className={styles.linkActions}>
                                <button
                                    type="button"
                                    className={styles.linkSubmitBtn}
                                    onMouseDown={(e) => {
                                        e.preventDefault()
                                        handleLinkSubmit()
                                    }}
                                >
                                    Apply
                                </button>
                                <button
                                    type="button"
                                    className={styles.linkCancelBtn}
                                    onMouseDown={(e) => {
                                        e.preventDefault()
                                        closeLinkPopup()
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </span>

                {/* Clear formatting */}
                <span className={styles.toolbarGroup}>
                    <button
                        className={styles.toolbarBtn}
                        onMouseDown={handleClean}
                        title="Clear formatting"
                        type="button"
                    >
                        <Icon
                            icon="ri:format-clear"
                            width={ICON_SIZE}
                            height={ICON_SIZE}
                        />
                    </button>
                </span>
            </div>
            ) : null}

            <AppLinkPicker
                open={appLinkPickerOpen}
                items={appLinkItems}
                loading={appLinkLoading}
                onSelect={handleAppLinkSelect}
                onClose={closeAppLinkPicker}
            />

            {/* Hidden image picker for Insert → Image */}
            <input
                ref={imageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml,image/bmp,image/x-icon,.png,.jpg,.jpeg,.gif,.webp,.svg,.bmp,.ico"
                style={{ display: 'none' }}
                onChange={handleImageFile}
            />

            <MediaViewer
                mediaFiles={mediaViewer.files}
                open={mediaViewer.open}
                initialIndex={mediaViewer.index}
                showThumbnails={false}
                onOpenChange={(open) =>
                    setMediaViewer((prev) => ({
                        ...prev,
                        open,
                        ...(open ? {} : { files: [], index: 0 }),
                    }))
                }
            />
        </div>
    )
}

export default DescriptionEditor
